// The provider call ledger (CHT-755 and CHT-759; contract `docs/architecture/milestone-e-payments-contract.md`, section 1.5). One row
// per money-moving provider call (`createOrder`, `createRefund`), written in its own transaction BEFORE the network call. A timeout
// then has a defined recovery: the row says a call may be in flight, and nothing creates a second order or an expiry until it is
// resolved. The database enforces the same rules (LF059); this file is the pure side the use cases read.
//
//   CALLING ──success──▶ SUCCEEDED (final)
//      │ ──terminal or certain-not-sent error──▶ REJECTED (final)
//      └─ timeout, 5xx, unknown ──▶ AMBIGUOUS ──resolve: found and equal──▶ SUCCEEDED
//                                         └──resolve: not found──▶ REJECTED
export const paymentProviderCallKinds = Object.freeze(["CREATE_ORDER", "CREATE_REFUND"]);
export const paymentProviderCallStates = Object.freeze([
    "CALLING",
    "AMBIGUOUS",
    "SUCCEEDED",
    "REJECTED",
]);
export const paymentProviderCallErrorClasses = Object.freeze([
    "RETRYABLE",
    "AMBIGUOUS",
    "TERMINAL",
]);
/** A `CALLING` row older than this, by the database clock, is read as `AMBIGUOUS`: the process may have died. */
export const PAYMENT_PROVIDER_CALL_STALE_MS = 120_000;
/** `outcome_code`: lowercase letters and underscores, at most 64. A code, never a vendor message. */
export const PAYMENT_PROVIDER_CALL_OUTCOME_PATTERN = /^[a-z_]{1,64}$/;
/** States a call can still change from. The other two are final. */
export const paymentProviderCallOpenStates = Object.freeze(["CALLING", "AMBIGUOUS"]);
export const isProviderCallOpen = (state) => state === "CALLING" || state === "AMBIGUOUS";
/** The states that count as "live" in the unique index: a new attempt is possible only after `REJECTED`. */
export const isProviderCallLive = (state) => state !== "REJECTED";
/**
 * What a reader sees: a `CALLING` row that is older than the stale window is `AMBIGUOUS`. The next writer moves the row to
 * `AMBIGUOUS` first. `now` is the database transaction time.
 */
export function providerCallStateAt(call, now) {
    if (call.state === "CALLING" &&
        now.getTime() - call.startedAt.getTime() >= PAYMENT_PROVIDER_CALL_STALE_MS) {
        return "AMBIGUOUS";
    }
    return call.state;
}
/** The allowed moves of a call row. Final states move nowhere. */
export const providerCallTransitions = Object.freeze({
    CALLING: Object.freeze(["SUCCEEDED", "REJECTED", "AMBIGUOUS"]),
    AMBIGUOUS: Object.freeze(["SUCCEEDED", "REJECTED"]),
    SUCCEEDED: Object.freeze([]),
    REJECTED: Object.freeze([]),
});
export const canMoveProviderCall = (from, to) => providerCallTransitions[from].includes(to);
/** The idempotency key of an order creation attempt (`order:<paymentRequestId>:<attempt>`). */
export const orderIdempotencyKey = (paymentRequestId, attempt) => `order:${paymentRequestId}:${attempt}`;
/** The idempotency key of a refund (`refund:<refundId>`), the same for every attempt of that refund. */
export const refundIdempotencyKey = (refundId) => `refund:${refundId}`;
// --- Reconciliation runs (CHT-758) ----------------------------------------------------------------------------------------------
/** What one reconciliation run looked at: one payment request, a time window of the provider's payments, or the expiry sweep. */
export const paymentReconciliationRunScopes = Object.freeze([
    "PAYMENT",
    "WINDOW",
    "EXPIRY_SWEEP",
]);
export const paymentReconciliationRunStates = Object.freeze(["RUNNING", "DONE", "FAILED"]);
/** The longest window of one reconcile-by-window run: 24 hours. */
export const PAYMENT_RECONCILIATION_MAX_WINDOW_MS = 24 * 60 * 60 * 1000;
